import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import _capitalize from 'lodash.capitalize';
import React, { useCallback, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import styled from 'styled-components';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../Button';
import { BaseModal } from '../modals/BaseModal';

type BaseModalProps = React.ComponentProps<typeof BaseModal>;

interface ModalWizardProps<FormValues, InitialFormValues> {
  addArrayMutators?: boolean;
  initialValues?: InitialFormValues;
  isEditing: boolean;
  isNextButtonDisabled?: boolean;
  itemLabel: string;
  modalMaxWidth?: BaseModalProps['maxWidth'];
  onClose: () => void;
  onNextPage?: (values: Partial<FormValues>, pageNumber: number) => void;
  onPageSubmit: (values: FormValues) => Promise<void>;
  parentHandlesSuccessAndError?: boolean;
  showBackButton?: boolean;
  startPage?: number;
  title: string;
  titleIcon?: React.ReactElement;
  wizardPages: React.ReactElement[];
}

interface ActionButtonsParams {
  disabled?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  isSubmitting: boolean;
}

const StyledPageNumberContainer = styled.div`
  position: relative;
  display: flex;
  width: fit-content;
`;

const StyledPageNumber = styled.div<{ $isActive: boolean }>`
  z-index: 1;
  padding: 0.2rem 0.45rem;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background-color: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.green : theme.colors.graybbb};
  border-radius: 16px;
  transform: scale(0.8);

  &:not(:first-child) {
    margin-left: 2rem;
  }
`;

const StyledPageNumberConnector = styled.div`
  position: absolute;
  top: 11px;
  left: 3px;
  width: calc(100% - 6px);
  border-top: 1px solid ${({ theme }) => theme.colors.graybbb};
`;

export function ModalWizard<
  FormValues,
  InitialFormValues = Partial<FormValues>,
>({
  addArrayMutators = false,
  initialValues,
  isEditing,
  isNextButtonDisabled,
  itemLabel,
  modalMaxWidth = 'md',
  onClose,
  onNextPage,
  onPageSubmit,
  parentHandlesSuccessAndError,
  showBackButton,
  startPage = 0,
  title,
  titleIcon,
  wizardPages,
}: ModalWizardProps<FormValues, InitialFormValues>) {
  const [wizardPageNum, setWizardPageNum] = useState(startPage);

  const isLastPage = wizardPageNum === wizardPages.length - 1;
  const startsOnLastPage = startPage === wizardPages.length - 1;
  const isFirstPage = wizardPageNum === 0;

  const handleNextPage = useCallback(() => {
    setWizardPageNum(wizardPageNum + 1);
  }, [wizardPageNum]);
  const handlePreviousPage = useCallback(() => {
    setWizardPageNum(wizardPageNum - 1);
  }, [wizardPageNum]);

  // Final-form resets its pristine status depending on which form fields are currently rendered,
  // this means that we can get pristine status only for the current modal page,
  // and have to track pristine statuses of the previous pages ourselves.
  const [previousPagePristineStatuses, setPreviousPagePristineStatuses] =
    useState(Array(wizardPages.length - 1).fill(true));
  const arePreviousPagesPristine = useMemo(
    () => previousPagePristineStatuses.every((pristine) => pristine),
    [previousPagePristineStatuses],
  );

  const { showUserFriendlyErrorToast, success: showSuccess } = useToast();
  const onSubmit = useCallback(
    async (
      values: FormValues,
      form: FormApi<FormValues, InitialFormValues>,
    ) => {
      if (!isLastPage) {
        const isCurrentPagePristine = form.getState().pristine;
        const newPristineStatuses = [...previousPagePristineStatuses];
        newPristineStatuses[wizardPageNum] = isCurrentPagePristine;
        setPreviousPagePristineStatuses(newPristineStatuses);

        onNextPage && onNextPage(values, wizardPageNum);
        handleNextPage();
        return;
      }
      if (parentHandlesSuccessAndError) {
        await onPageSubmit(values);
      } else {
        try {
          await onPageSubmit(values);
          showSuccess(
            `${_capitalize(itemLabel)} has been ${
              isEditing ? 'edited.' : 'created.'
            }`,
          );
          onClose();
        } catch (error) {
          showUserFriendlyErrorToast(error, `Unable to save ${itemLabel}.`, {
            dontAutoHide: true,
          });
        }
      }
    },
    [
      handleNextPage,
      isEditing,
      isLastPage,
      itemLabel,
      onClose,
      onNextPage,
      onPageSubmit,
      parentHandlesSuccessAndError,
      previousPagePristineStatuses,
      showSuccess,
      showUserFriendlyErrorToast,
      wizardPageNum,
    ],
  );

  const getButtons = useCallback<
    (params: ActionButtonsParams) => React.ReactElement
  >(
    ({ disabled = false, onClick, isSubmitting }) => {
      const getSaveButtonText = () => {
        if (isSubmitting) return 'Saving...';
        return isLastPage ? 'Save' : 'Next';
      };

      return (
        <>
          {!isFirstPage && showBackButton && (
            <Button color="primary" onClick={handlePreviousPage} type="button">
              Back
            </Button>
          )}
          <Button
            color="primary"
            disabled={disabled}
            onClick={onClick}
            type="submit"
            variant="contained"
          >
            {getSaveButtonText()}
          </Button>
        </>
      );
    },
    [handlePreviousPage, isFirstPage, isLastPage, showBackButton],
  );

  const hidePageNumbers = useMemo(
    () => wizardPages.length === 1 || (startsOnLastPage && !showBackButton),
    [showBackButton, startsOnLastPage, wizardPages.length],
  );

  return (
    <Form<FormValues, InitialFormValues>
      initialValues={initialValues}
      // @ts-expect-error - arrayMutators from final-form-arrays has `any` in its type, and our form expects `FormValues`
      mutators={addArrayMutators ? { ...arrayMutators } : undefined}
      onSubmit={onSubmit}
      subscription={{
        invalid: true,
        pristine: true,
        submitting: true,
      }}
    >
      {({ handleSubmit, invalid, pristine, submitting }) => (
        <form onSubmit={handleSubmit}>
          <BaseModal
            buttons={getButtons({
              disabled:
                (!isLastPage && isNextButtonDisabled) ||
                (isLastPage && pristine && arePreviousPagesPristine) ||
                (pristine && invalid) ||
                submitting,
              isSubmitting: submitting,
              onClick: (e) => handleSubmit(e),
            })}
            actionsLeft={
              hidePageNumbers ? undefined : (
                <StyledPageNumberContainer>
                  {[...Array(wizardPages.length)].map((value, index) => (
                    <StyledPageNumber
                      key={`page-number-${value + 1}`}
                      $isActive={index === wizardPageNum}
                    >
                      {index + 1}
                    </StyledPageNumber>
                  ))}
                  <StyledPageNumberConnector />
                </StyledPageNumberContainer>
              )
            }
            icon={titleIcon}
            maxWidth={modalMaxWidth}
            onClose={onClose}
            open
            showCloseButton
            title={title}
          >
            {wizardPages[wizardPageNum]}
          </BaseModal>
        </form>
      )}
    </Form>
  );
}
